import _ from 'lodash';
import { makeAutoObservable, observable } from 'mobx';
import moment from 'moment';
import { ButtonComponentType } from '../../component/button/button.component';
import GlobalNotification from '../../component/common/snackbar';
import { HeadCell } from '../../component/table/table.component';
import i18n from '../../i18n';
import { Condition, Group, RestaurantsInGroup, Warrant, WarrantCode } from '../../models';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import LSM from '../../modules/cache/tool-local-storage';
import apiManage from '../../request';
import { groupUnitData } from './group-unit';
import { ConditionType } from '../../component/form/search-key-input.component';
import { WillStopConfig, createPageReqDocker, removeReq } from '../../request/http';
import { getAuthPointI18n } from '../../constants/tool-auth-point';


// 集团数据管理
export class GroupState implements GroupData {

    constructor() {
        makeAutoObservable(this);
    }

    // *********************
    // Attributes
    // *********************

    // 语言
    language: string = LSM.get(LOCAL_STORAGE_KEYS.LANGUAGE) || 'EN';
    // 点击集团列表行详情按钮进去的数据
    enterGroupDetailObj: {
        key: number;
        groupData: Data
    } = {
            // 进入集团详情索引
            key: -1,
            // 进入集团详情数据
            groupData: {}
        };
    // 显示集团账号详情
    showUnit: boolean = false;
    // 集团数据列表
    groupsList = observable<Data>([]);
    // 餐馆模式下表格数据列表
    restaurantList = observable<RestaurantsInGroup>([]);
    // 搜索框内容
    searchValue: string = '';
    // 下拉框默认值 左边条件搜索下拉框
    selectValue: string = 'nickname';
    // 筛选授权过期时间下拉框默认值
    auFilterSelectValue: AuDateFilterSelectType | string = '0';
    // 右边授权模式下拉框默认值
    auSelectValue: AuModeSelectType = 'group';
    // 表格加载
    isTableLoading: boolean = false;
    // 新建集团弹窗标识
    isOpenDialog: boolean = false;
    // 弹窗加载loading
    isLoading: boolean = false;
    // 集团额外传参
    extraParams: Group & Condition = {};
    // 排序参数
    extraSortParams: {
        sort_key: string,
        sort_value: number
    } = { sort_key: '', sort_value: 0 };
    // 删除弹窗配置
    deleteDialogConfig: {
        // 打开弹窗
        visible: boolean;
        // 弹窗标题
        title?: string;
        // 弹窗说明
        desc?: string;
        // 是否禁用确认按钮
        confirmDisabled?: boolean;
        // 关闭弹窗
        handleClick?: () => void;
    } = {
            visible: false,
            title: '',
        }
    // 授权点数组
    auPointList: { value: WarrantCode, label: string }[] = [];
    // 授权点选择的值
    auPointSelectValue: string[] = [];
    // 业务标签筛选的值
    businessLabelValues: string[] = [];
    // 授权点集合对象
    auPointsMap: { warrantPoint: Warrant[] } = {
        warrantPoint: []
    }
    // 是否有更多数据
    isNoMoreData = false;
    // 表格加载中
    isTableLoadingNext = false;
    // 表格行功能按钮键map
    tableRowButtonsMap: Record<'group' | 'restaurant', (row: Group | RestaurantsInGroup, index: number) => ButtonComponentType[]> = {
        'group': (row: Group, index: number) => [{
            extraButtonStyle: 'iconButton btn_delete_group',
            variant: 'contained',
            color: 'secondary',
            size: 'small',
            action: 'DELETE',
            handleClick: () => {
                // 当前集团是否存在关联餐厅
                const hasRestaurants = this.hasRestaurants(row);
                this.updateDeleteDialogConfig({
                    visible: true,
                    title: i18n.t('general_text_tips'),
                    desc: i18n.t(
                        hasRestaurants
                            ? 'group_management_page_text_group_has_restaurants_cannot_delete'
                            : 'group_management_page_text_confirm_delete_group',
                        { groupName: row.nickname || '' }
                    ),
                    confirmDisabled: hasRestaurants,
                    handleClick: hasRestaurants
                        ? async () => {
                            // 关闭不可删除提示弹窗，不触发删除接口
                            this.updateDeleteDialogConfig({ visible: false });
                        }
                        : async () => {
                            if (row?._id) {
                                await this.handleDelete(row, false)
                            }
                        }
                })
            }
        }, {
            extraButtonStyle: 'iconButton btn_edit_group',
            size: 'small',
            variant: 'contained',
            action: 'VIEW',
            color: 'primary',
            handleClick: () => {
                this.enterUnit(row, index);
                groupUnitData.updateGroupId(row);
                groupUnitData.updatePanel(0);
            }
        }],
        'restaurant': (row: RestaurantsInGroup, index: number) => [
            {
                extraButtonStyle: 'iconButton btn_edit_group',
                size: 'small',
                variant: 'contained',
                action: 'VIEW',
                color: 'primary',
                handleClick: () => {
                    // 组装正确访问的数据
                    const groupData: Group = {
                        _id: row.belonged_group_id ?? '',
                        string_id: row?.belonged_group_string_id ?? '',
                        nickname: row?.belonged_group_nickname
                    }
                    this.enterUnit(groupData, index);
                    groupUnitData.updateGroupId(groupData);
                    groupUnitData.updatePanel(0);
                }
            }
        ]
    }
    // 获取授权数据map
    getDataFuncMap = {
        //
        /**
         * 授权模式  查找数据
         * @param dateType 授权到期类型筛选值
         * @param modeType 授权模式筛选值
         */
        'AuMode': async (dateType: AuDateFilterSelectType, modeType: AuModeSelectType, extraParam?: Group & Condition) => {
            await this.getGroupListData(dateType, extraParam);
        }
    }
    // 当天日期
    today: string = moment().format('YYYY-MM-DD HH:mm:ss');
    // 搜索条件
    conditions = observable<ConditionType>([]);
    // 每页数量
    perPage = 20;
    // 当前页码
    page = 0;

    // 设置搜索条件
    setConditions(val: ConditionType[]) {
        // fiskaly_environment是单选 如果key 为 fiskaly_environment"有两个只拿最新那个
        if (val.some((item) => item.key === 'fiskaly_environment')) {
            val = val.filter((item) => item.key === 'fiskaly_environment').slice(-1);
        }

        this.conditions.replace(val);
        const extraParams = {};
        val.forEach((item) => {
            extraParams[item.key] = extraParams[item.key] ? _.concat(extraParams[item.key], item.value) : [item.value];
        })

        this.setExtraParams(extraParams);
        this.getDataFuncMap['AuMode'](this.auFilterSelectValue as AuDateFilterSelectType, this.auSelectValue, extraParams);

    }

    // 设置筛选条件为空
    setEmptyConditions(val: ConditionType[]) {
        this.conditions.replace(val);
    }

    // 获取表头排序状态值
    getSortStatus(key: string) {
        const listSort = this.extraSortParams;
        if (key === listSort.sort_key) {
            return listSort.sort_value
        }
        return 0;
    }

    // 更新集团数组
    updateGroups(newArr: Array<Data>) {
        this.groupsList.replace(newArr);
    }

    // 更新餐馆数据数组
    updateRestaurant(newArr: Array<RestaurantsInGroup>) {
        this.restaurantList.replace(newArr);
    }

    // 初始化
    async init() {
        this.getStaticWarrants();
    }

    // 搜索表格
    async handleSearch(searchVal: string): Promise<void> {
        const key = this.selectValue;
        let extraParams = {};
        // 搜索输入框需要有值
        if (searchVal !== '') {
            extraParams = {
                [key]: searchVal,
            }
        }
        this.setExtraParams(extraParams);
        this.getDataFuncMap['AuMode'](this.auFilterSelectValue as AuDateFilterSelectType, this.auSelectValue, extraParams);
    }

    // 表格排序
    async handleSort(headCell: HeadCell) {
        // 排序值 -1 不显示排序的值高亮 ， 0 不排序  1 降序 2 升序
        const sortValue = headCell?.sortStatus === 0 ? 1 : headCell?.sortStatus === 1 ? 2 : 0;
        // 如果是0表示不排序
        let params = { sort_key: '', sort_value: 0 };
        if (sortValue !== 0) {
            params = {
                sort_key: 'nickname',
                sort_value: sortValue
            }
        }
        // 设置传参
        this.setExtraSortParams(params);
        // 请求数据
        await this.getGroupListData(this.auFilterSelectValue);
    }

    /**
     * 设置获取集团数据额外传参
     * @param extraParam
     */
    setExtraParams(extraParam: Group & Condition) {
        this.extraParams = extraParam;
    }

    /**
     * 设置排序参数
     * @param val
     */
    setExtraSortParams(val: {
        sort_key: string,
        sort_value: number
    }) {
        this.extraSortParams = val;
    }

    /**
     * 设置搜索值
     * @param val 搜索值
     */
    setSearchValue(val: string) {
        this.searchValue = val;
    }

    /**
     * 设置下拉框的值 左边搜索条件下拉框
     * @param value
     */
    async setSelectValue(value: string) {
        this.selectValue = value;
        // switch (value) {
        //     case 'warrants':
        //         // 切换时  清空之前的筛选值
        //         this.setBusinessEmptyValues([]);
        //         // 选择授权方案时默认筛选日期选择的值为第一个
        //         this.setAuFilterSelectValue('0');
        //         // 如果选择授权方案筛选  清空之前搜索框搜索的值
        //         if (this.searchValue !== '') {
        //             this.setExtraParams({});
        //         }
        //         break;
        //     case 'business_labels':
        //         // 切换时  清空之前的筛选值
        //         this.setAuPointArr([]);
        //         // 选择授权方案时默认筛选日期选择的值为第一个
        //         this.setAuFilterSelectValue('0');
        //         // 如果选择授权方案筛选  清空之前搜索框搜索的值
        //         if (this.searchValue !== '') {
        //             this.setExtraParams({});
        //         }
        //         break;
        //     default:
        //         // 筛选条件不为授权方案  清空多选框选择的值  不请求接口
        //         this.setAuPointArr([]);
        //         this.setBusinessEmptyValues([]);
        //         break;
        // }
    }

    /**
     * 获取授权静态资源数据
     */
    async getStaticWarrants() {
        // 请求接口
        const auPointResult: any = await apiManage.get_static_warrant({ key: this.language === 'CN' ? 'CN' : 'EN' });
        const { warrantPoint, warrantCollection } = auPointResult as { warrantPoint: Warrant[], warrantCollection: Warrant[] };
        // 经销商可销售授权包包含的授权点
        const warrantCollectionAll: Warrant[] = (warrantCollection||[]).reduce((pre: Warrant[], cur: Warrant) => {
            return [...pre, ...cur.warrants as Warrant[]];
        }, [] as Warrant[]);
        // 去重
        const warrantPointAllArr: Warrant[] = _.uniqBy([...warrantPoint||[], ...warrantCollectionAll||[]], 'code');
        this.setAuPointsMap({ warrantPoint: warrantPointAllArr });
    }

    /**
     * 设置授权点集合对象
     * @param value
     */
    setAuPointsMap(value: typeof this.auPointsMap) {
        this.auPointsMap = value;
        this.setAuPointList(value.warrantPoint);
    }

    /**
     * 设置授权点选择的值  设置为空  不请求接口
     * @param value
     */
    setAuPointArr(value: string[]) {
        this.auPointSelectValue = value;
    }

    /**
     * 设置授权点选择的值  设置为空  不请求接口
     * @param value
     */
    setBusinessEmptyValues(value: string[]) {
        this.businessLabelValues = value;
    }

    /**
     * 设置授权点选择的值  每次改变就请求接口
     * @param value
     */
    async setAuPointSelectVal() {
        this.getDataFuncMap['AuMode'](this.auFilterSelectValue as AuDateFilterSelectType, this.auSelectValue);
    }

    /**
     * 设置业务标签选择的值
     * @param value
     */
    setBusinessSelectValues() {
        this.getDataFuncMap['AuMode'](this.auFilterSelectValue as AuDateFilterSelectType, this.auSelectValue);
    }

    /**
     * 设置授权点集合筛选下拉框
     * @param pointWarrants
     */
    setAuPointList(pointWarrants: Warrant[]) {
        const auPointLists: { value: WarrantCode, label: string }[] = [];
        pointWarrants.forEach(item => {
            // 筛选出对应国际化授权名字
            const filterI18nName = getAuthPointI18n(item?.warranti18N?.name || []);
            auPointLists.push({
                value: item.code || 'AU1002',
                label: filterI18nName,
            })
        });
        this.auPointList = auPointLists;
    }

    /**
     * 设置下拉框的值 右边筛选条件下拉框 授权筛选模式
     * @param value
     */
    async setAuSelectValue(value: AuModeSelectType) {
        this.auSelectValue = value;
    }

    /**
     * 设置下拉框的值 筛选授权到期时间的数据
     * @param value
     */
    async setAuFilterSelectValue(value: string) {
        this.auFilterSelectValue = value;
        await this.getDataFuncMap['AuMode'](value as AuDateFilterSelectType, this.auSelectValue);
    }

    // 切换到餐馆授权模式
    setTableDataList(groupDataSource: Group[]) {
        const groupList = _.cloneDeep(groupDataSource);
        // 餐厅数据
        const resList: RestaurantsInGroup[] = [];
        // 取出所有集团下的所有餐馆数据
        groupList.forEach((groupItem) => {
            // 过滤集团授权点
            groupItem!.warrants = this.filterDateWarrants(groupItem?.warrants || []);
            // 过滤餐厅授权
            groupItem!.restaurants = this.filterRestaurantWarrants(groupItem?.restaurants || []);
            groupItem?.restaurants?.forEach(resItem => {
                // 取出餐馆模式下需要展示的数据
                resList.push({
                    ...resItem,
                    warrants: this.filterDateWarrants(resItem?.warrants || []),
                    belonged_group_nickname: groupItem.nickname,
                    belonged_group_string_id: groupItem.string_id,
                    belonged_group_id: groupItem._id,
                    authorization_type: resItem.authorization_type || '',
                    business_labels: resItem?.business_labels || [],
                    belonged_reseller: groupItem?.belonged_reseller || []

                });
            })
        });
        this.updateGroups(groupList);
        this.updateRestaurant(resList);
    }

    // 过滤授权点
    filterDateWarrants(filterWarrants: Warrant[]) {
        try {
            // 现在的时间
            const now = moment();
            filterWarrants.forEach(warrantItem => {
                const endDate = moment(warrantItem?.limit_point?.end_date);
                // 计算当前时间与给定时间之间的时差和分钟差
                // const diffInHours = now.diff(endDate, 'hours');
                const diffInMinutes = now.diff(endDate, 'minutes');
                // 计算总的时间差（精确到分钟）
                const diffDuration = moment.duration(diffInMinutes, 'minutes');
                //  已到期
                if (now.isAfter(endDate, 'minute')) {
                    warrantItem['expireType'] = 'was_due';
                }
                // 未到期
                if (endDate.diff(now, 'day') > 90) {
                    warrantItem['expireType'] = 'not_yet_due';
                    return;
                }
                // 临近到期 条件需为未到期和小于九十天
                if (endDate.diff(now, 'day', true) > 0 && diffDuration.asDays() < 90) {
                    warrantItem['expireType'] = 'soon';
                }
            });
            return filterWarrants;
        } catch (e) {
            return []
        }
    }

    // 过滤餐厅授权点
    filterRestaurantWarrants(restaurants: RestaurantsInGroup[]) {
        restaurants = restaurants.filter((item) => item.string_id)
        restaurants.forEach(item => {
            item.warrants = this.filterDateWarrants(item.warrants || []);
        });
        return restaurants;
    }

    // 表格加载
    loadTable() {
        return this.isTableLoading = true;
    }

    // 加载完毕
    loadedTable() {
        return this.isTableLoading = false;
    }

    /**
     * 获取集团列表数据
     * @param filter_authorize 授权到期类型筛选值
     * @param extraParam 集团数据获取额外传参 有排序和搜索值
     */
    async getGroupListData(filter_authorize?: string, extraParam?: Group & Condition) {
        this.page = 0;
        this.setTableLoadingNext(false)
        this.setNoMoreData(false)
        // 取消上个请求
        removeReq.call(this)
        // 创建取消请求拦截器
        createPageReqDocker.call(this)
        const sortObj = this.extraSortParams.sort_value ? {
            sort_key: this.extraSortParams.sort_key,
            sort_value: this.extraSortParams.sort_value === 2 ? -1 : this.extraSortParams.sort_value,
        } : {};

        const filterAuthorizeParams = filter_authorize || this.auFilterSelectValue;

        // 搜索条件
        const otherParams = {
            // 筛选授权的值：| ALL 已到期 未到期 临近到期 无授权 | 对应=> | 0 1 2 3 4 | 默认 0
            filter_authorize: Number(filterAuthorizeParams) || 0,
            ...this.extraParams,
            // 搜索的传参
            ...extraParam,
        }
        // 搜索条件为空不发起请求
        if (_.isEqual({}, otherParams) || _.isEqual({ filter_authorize: 0 }, otherParams)) {
            return;
        }

        // 开启加载
        this.loadTable();

        /**
         * 参数 params
         * @param pagination_per_page 这里取最大 拿到所有数据
         * @param key 这个字段是传给后端，用于筛选授权的数据，使用对应国家的国际化
         */
        const params = {
            key: this.language === 'CN' ? 'CN' : 'EN',
            pagination_per_page: this.perPage,
            pagination_page: this.page + 1,
            ...otherParams,
            // 排序的传参
            ...sortObj,
        };
        // 请求集团接口
        const result = await apiManage.get_group_all_warrant({ ...params }, {
            // api太慢。请求下一个时需要取消上个请求
            stop_by_unmount: true
        } as WillStopConfig);
        // 请求被取消
        if (result.message === 'canceled') {
            return
        }
        // 获取成功
        if (!result.error) {
            // 解构数据
            const { dbResults } = result[0];
            this.setTableDataList(dbResults || []);
        } else {
            // 获取失败
            GlobalNotification.error(i18n.t('group_management_page_text_failed_get_group_list'));
        }
        // 关闭加载
        this.loadedTable();
    }

    /**
     * 加载更多数据
     */
    async handleLoadMore() {
        if (this.isNoMoreData || this.isTableLoadingNext) {
            return;
        }
        this.page = this.page + 1;
        this.fetchData(this.page);
    }

    /**
     * 滚动加载表格数据
     * @param page 
     * @returns 
     */
    async fetchData(page: number) {
        const sortObj = this.extraSortParams.sort_value ? {
            sort_key: this.extraSortParams.sort_key,
            sort_value: this.extraSortParams.sort_value === 2 ? -1 : this.extraSortParams.sort_value,
        } : {};

        // 搜索条件
        const otherParams = {
            // 筛选授权的值：| ALL 已到期 未到期 临近到期 无授权 | 对应=> | 0 1 2 3 4 | 默认 0
            filter_authorize: Number(this.auFilterSelectValue) || 0,
            ...this.extraParams,
        }
        // 搜索条件为空不发起请求
        if (_.isEqual({}, otherParams) || _.isEqual({ filter_authorize: 0 }, otherParams)) {
            return;
        }


        /**
         * 参数 params
         * @param pagination_per_page 这里取最大 拿到所有数据
         * @param key 这个字段是传给后端，用于筛选授权的数据，使用对应国家的国际化
         */
        const params = {
            key: this.language === 'CN' ? 'CN' : 'EN',
            pagination_per_page: this.perPage,
            pagination_page: page + 1,
            ...otherParams,
            // 排序的传参
            ...sortObj,
        };
        // 请求集团接口
        this.setTableLoadingNext(true)
        const result = await apiManage.get_group_all_warrant({ ...params });
        this.setTableLoadingNext(false)
        if (result.message === 'canceled') {
            return
        }
        // 获取成功
        if (!result.error) {
            // 解构数据
            const { dbResults } = result[0];
            if (dbResults.length) {
                const newData = [...this.groupsList, ...dbResults]
                this.setTableDataList(newData);
                this.setNoMoreData(false)
            } else {
                this.setNoMoreData(true)
            }

        } else {
            // 获取失败
            GlobalNotification.error(i18n.t('group_management_page_text_failed_get_group_list'));
        }
    }


    // 打开新建集团弹窗
    openAddGroupDialog() {
        this.isOpenDialog = true;
    }

    // 关闭新建集团弹窗
    closeAddGroupDialog() {
        this.isOpenDialog = false;
    }

    /**
     * 更新删除弹窗数据
     * @param deleteConfigData 弹窗配置参数
     */
    updateDeleteDialogConfig(deleteConfigData: typeof this.deleteDialogConfig) {
        this.deleteDialogConfig = deleteConfigData;
    }

    /**
     * 判断集团是否存在关联餐厅。
     * @param group 集团数据
     * @returns 存在关联餐厅时返回 true
     */
    hasRestaurants(group: Group): boolean {
        return (group.restaurants || []).length > 0 || (group.owned_restaurant || []).length > 0;
    }

    /**
     * 生成批量删除弹窗说明，提前告知用户删除限制。
     * @param groups 当前选中的集团
     * @returns 批量删除弹窗说明文案
     */
    getBatchDeleteDialogDescription(groups: Group[]): string {
        const groupNames = groups.map((group) => group.nickname || '').filter(Boolean).join('、');
        const blockedGroupNames = groups
            .filter((group) => this.hasRestaurants(group))
            .map((group) => group.nickname || '')
            .filter(Boolean)
            .join('、');
        return i18n.t(
            blockedGroupNames
                ? 'group_management_page_text_group_has_restaurants_cannot_delete'
                : 'group_management_page_text_confirm_delete_group',
            { groupName: blockedGroupNames || groupNames }
        );
    }

    /**
     * 删除集团
     * @param deleteData 删除的数据
     * @param isBatch 是否为批量删除 不是则调用单独删除
     */
    async handleDelete(deleteData: Group[] | Group, isBatch: boolean): Promise<void> {
        /**
         * 1.删除集团
         * 2.成功之后重新请求数据
         * @argument isBatch 是否为批量删除 不是则调用单独删除
         */
        try {
            // 含关联餐厅的集团不允许删除，批量场景整批拦截
            const groups = Array.isArray(deleteData) ? deleteData : [deleteData];
            const blockedGroups = groups.filter((group) => this.hasRestaurants(group));
            if (blockedGroups.length) {
                const groupNames = blockedGroups.map((group) => group.nickname || '').filter(Boolean).join('、');
                GlobalNotification.error(i18n.t(
                    'group_management_page_text_group_has_restaurants_cannot_delete',
                    { groupName: groupNames }
                ));
                return;
            }

            // 根据参数选择传参
            const sendParam = isBatch ? (deleteData as Group[] ?? []).map(item => item._id) : (deleteData as Group ?? {})._id ?? '';
            // 请求数据  判断是否为批量删除  不是则调用单独删除
            const result = isBatch ? await apiManage.delete_group(sendParam as string[]) : await apiManage.delete_group_id_id(sendParam as string);
            // 请求成功
            if (!result?.error) {
                // 成功提示
                GlobalNotification.success(`${i18n.t(isBatch ? 'general_message_text_batch_delete_successfully' : 'group_management_page_text_group_successfully_deleted')}`);
                // 刷新数据
                await this.getGroupListData(this.auFilterSelectValue);
            } else {
                // 失败
                GlobalNotification.error(`${result?.status}: ${result?.result?.message} `);
            }
        } catch (error) {
            GlobalNotification.error(`${i18n.t('general_message_text_delete_failed')}`);
        }
        // 关闭弹窗
        this.updateDeleteDialogConfig({
            visible: false
        });
    }

    /**
     * 进入相应点击的集团item
     * @param rowData 集团列表行数据
     * @param rowDataIndex 接团列表行索引
     */
    enterUnit(rowData: Data, rowDataIndex: number) {
        this.showUnit = true;
        this.enterGroupDetailObj = {
            key: rowDataIndex,
            groupData: rowData
        };
        // 清空筛选条件
        this.clearFilterData();
    }

    // 返回集团管理列表
    async goBack() {
        this.showUnit = false;
        // 并刷新数据
        await this.init();
    }

    // 清空重置筛选条件
    clearFilterData() {
        this.setExtraSortParams({
            sort_key: '', sort_value: 0
        });
        this.setExtraParams({});
        // 清空搜索值
        this.setSearchValue('');
        // 授权到期默认值设置为默认
        this.auFilterSelectValue = '0';
        // 清空联合搜索的数据
        this.setEmptyConditions([]);
    }

    // 开启加载  弹窗loading
    load() {
        this.isLoading = true
    }

    // 关闭加载  弹窗loading
    loaded() {
        this.isLoading = false;
    }

    // 设置表格加载
    setTableLoadingNext(loading: boolean) {
        this.isTableLoadingNext = loading;
    }

    // 设置没有更多数据
    setNoMoreData(loading: boolean) {
        this.isNoMoreData = loading;
    }
}

// 集团管理页数据
type GroupData = {
    // 集团数据
    groupsList: Array<Data>;
    // 搜索框内容
    searchValue: string;
    // 下拉框默认值
    selectValue: string;
    // 右边授权模式下拉框默认值
    auSelectValue: string;
    // 筛选授权过期时间下拉框
    auFilterSelectValue: string;
    // 表格加载
    isTableLoading: boolean;
    // 弹窗加载loading
    isLoading: boolean;
}

// $ TYPE
type Data = Group;

// 授权下拉框选择的值
export type AuModeSelectType = 'group' | 'restaurant';

// 授权过期类型筛选值  0全部授权 | 1已过期 | 2未过期 | 3即将过期 | 4无授权
type AuDateFilterSelectType = '0' | '1' | '2' | '3' | '4';